import React, { useState, useEffect, useRef, useContext } from 'react';
import { Container, Row, Col, Card, Table, Dropdown, Modal, Form, Button } from 'react-bootstrap';
import { FaEllipsisV, FaExclamationTriangle, FaCheckCircle, FaClock, FaTrash, FaPaperPlane } from 'react-icons/fa';
import { MdWarning } from 'react-icons/md';
import { BsRobot } from 'react-icons/bs';
import { FiClock, FiCheckCircle, FiActivity, FiAlertCircle, FiX } from 'react-icons/fi';
import AppPagination from "../../../components/general/pagination/Pagination.js";
import MasterLayout from '../../../layout/superadmin/MasterLayout';
import '../../../assets/css/superadmin/disputemanagment/DisputeManagment.css';
import AppContext from '../../../context/AppContext.js';
import axios from 'axios';
import Swal from 'sweetalert2';
import { getEcho } from '../../../utils/echo';
 
/* ══════════════════════════════════════════
   TABS CONFIG — mapped to the actual status
   values used across the app: pending, active, resolved
══════════════════════════════════════════ */
const TABS = [
  { label: 'All',         icon: <FiAlertCircle size={14} />, status: 'all'      },
  { label: 'Pending',     icon: <FiClock size={14} />,       status: 'pending'  },
  { label: 'In Progress', icon: <FiActivity size={14} />,    status: 'active'   },
  { label: 'Completed',   icon: <FiCheckCircle size={14} />, status: 'resolved' },
];

/* ══════════════════════════════════════════
   SKELETON COMPONENTS
══════════════════════════════════════════ */
const SkeletonStatCards = () => (
  <Row className="dsp-stats-row">
    {[1, 2, 3].map((i) => (
      <Col lg={4} md={6} className="mb-3" key={i}>
        <div className="dsp-skel-stat-card">
          <div className="dsp-skel dsp-skel-stat-icon" />
          <div className="dsp-skel-stat-text">
            <div className="dsp-skel dsp-skel-stat-label" />
            <div className="dsp-skel dsp-skel-stat-value" />
            <div className="dsp-skel dsp-skel-stat-change" />
          </div>
        </div>
      </Col>
    ))}
  </Row>
);

const SkeletonTabs = () => (
  <div className="dsp-skel-tabs-wrap">
    {[1, 2, 3, 4].map((i) => (
      <div key={i} className="dsp-skel dsp-skel-tab" />
    ))}
  </div>
);

const SkeletonTable = () => (
  <Card className="dsp-table-card">
    <Card.Body>
      <div className="dsp-skel-table-header">
        <div className="dsp-skel dsp-skel-table-title" />
      </div>
      <div className="dsp-skel-table-wrap">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="dsp-skel-table-row">
            <div className="dsp-skel dsp-skel-cell dsp-skel-cell-id" />
            <div className="dsp-skel-person-col">
              <div className="dsp-skel dsp-skel-cell-name" />
              <div className="dsp-skel dsp-skel-cell-sub" />
            </div>
            <div className="dsp-skel-person-col">
              <div className="dsp-skel dsp-skel-cell-name" />
              <div className="dsp-skel dsp-skel-cell-sub" />
            </div>
            <div className="dsp-skel dsp-skel-cell dsp-skel-cell-cat" />
            <div className="dsp-skel dsp-skel-badge" />
            <div className="dsp-skel dsp-skel-cell dsp-skel-cell-date" />
            <div className="dsp-skel dsp-skel-action" />
          </div>
        ))}
      </div>
    </Card.Body>
  </Card>
);

/* ══════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════ */
const Disputes = () => {
  const { baseUrl, authToken } = useContext(AppContext);

  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');

  const [disputes, setDisputes] = useState([]);

  const [showResolveModal, setShowResolveModal] = useState(false);
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [messageInput, setMessageInput] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [resolving, setResolving] = useState(false);
  const itemsPerPage = 5;

  const messagesEndRef = useRef(null);

  // ── Fetch all disputes forwarded to superadmin ──
  const viewForwardedDisputes = () => {
    axios.post(`${baseUrl}/superadmin/dispute/viewForwardedDisputes`, {}, {
      headers: { Authorization: `Bearer ${authToken}` }
    }).then((res) => {
      if (res.data.success) setDisputes(res.data.request || []);
    }).catch((err) => console.log(err)).finally(() => setIsLoading(false));
  };

  useEffect(() => {
    viewForwardedDisputes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── PUSHER — jab dispute chat modal khula ho, uski conversation
  //    ke naye messages real-time yahan aate rahenge ──
  useEffect(() => {
    if (!conversationId) return;

    const echo = getEcho(baseUrl);
    if (!echo) return;

    const channel = echo.private(`conversation.${conversationId}`);

    channel.listen(".new-message", (e) => {
      setChatMessages((prev) => [...prev, e.chat]);
    });

    return () => {
      echo.leave(`conversation.${conversationId}`);
    };
  }, [conversationId, baseUrl]);

  // ── Scroll to bottom on new message ──
  useEffect(() => {
    if (showResolveModal && messagesEndRef.current)
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, showResolveModal]);

  // ── Filter by active tab ──
  const filteredDisputes = activeTab === 'all'
    ? disputes
    : disputes.filter(d => d.status === activeTab);

  const totalPages = Math.ceil(filteredDisputes.length / itemsPerPage);
  const paginatedData = filteredDisputes.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // ── Fetch the group chat for the selected dispute ──
  const fetchChatForDispute = (dispute) => {
    axios.post(`${baseUrl}/superadmin/dispute/fetchChatForDispute`, {
      srdispute_id: dispute.id
    }, {
      headers: { Authorization: `Bearer ${authToken}` }
    }).then((res) => {
      if (res.data.success) {
        setChatMessages(res.data.chats || []);
        setConversationId(res.data.srconversation_id);
      } else {
        setChatMessages([]);
        setConversationId(null);
      }
    }).catch((err) => {
      console.log(err);
      setChatMessages([]);
      setConversationId(null);
    });
  };

  const handleResolveClick = (dispute) => {
    setSelectedDispute(dispute);
    setMessageInput('');
    setShowResolveModal(true);
    fetchChatForDispute(dispute);
  };

  const handleCloseModal = () => {
    setShowResolveModal(false);
    setSelectedDispute(null);
    setChatMessages([]);
    setConversationId(null);
    setMessageInput('');
  };

  // ── Send message into the group chat ──
  const handleSendMessage = () => {
    if (!messageInput.trim() || !conversationId) return;

    axios.post(`${baseUrl}/superadmin/dispute/sendMessageForDispute`, {
      srconversation_id: conversationId,
      message: messageInput,
    }, {
      headers: { Authorization: `Bearer ${authToken}` }
    }).then((res) => {
      if (res.data.success) {
        setMessageInput('');
        fetchChatForDispute(selectedDispute);
      }
    }).catch((err) => console.log(err));
  };

  // ── Mark as resolved ──
  const handleMarkAsResolved = () => {
    if (!selectedDispute) return;

    Swal.fire({
      title: 'Mark this dispute as resolved?',
      text: 'This will close the dispute for both parties.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, resolve it',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#FDB714',
    }).then((result) => {
      if (!result.isConfirmed) return;

      setResolving(true);

      axios.post(`${baseUrl}/superadmin/dispute/resolveDispute`, {
        srdispute_id: selectedDispute.id,
      }, {
        headers: { Authorization: `Bearer ${authToken}` }
      }).then((res) => {
        setResolving(false);
        if (res.data.success) {
          setDisputes(prev =>
            prev.map(d => d.id === selectedDispute.id ? { ...d, status: 'resolved' } : d)
          );
          Swal.fire({
            title: 'Resolved!',
            text: 'The dispute has been marked as resolved.',
            icon: 'success',
            confirmButtonColor: '#FDB714',
          });
          handleCloseModal();
        } else {
          Swal.fire({
            title: 'Failed',
            text: res.data.message || 'Could not resolve this dispute.',
            icon: 'error',
            confirmButtonColor: '#FDB714',
          });
        }
      }).catch((err) => {
        setResolving(false);
        console.log(err);
        Swal.fire({
          title: 'Error',
          text: 'Something went wrong while resolving this dispute.',
          icon: 'error',
          confirmButtonColor: '#FDB714',
        });
      });
    });
  };

  // ── Delete (local only — no delete route wired up yet) ──
  const handleDeleteDispute = (disputeId) => {
    Swal.fire({
      title: 'Are you sure?',
      text: `Delete dispute #${disputeId}? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#FDB714',
    }).then((result) => {
      if (!result.isConfirmed) return;
      setDisputes(prev => prev.filter(d => d.id !== disputeId));
    });
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'pending':  return 'dsp-status-action-required';
      case 'active':   return 'dsp-status-in-progress';
      case 'resolved': return 'dsp-status-resolved';
      default:         return 'dsp-status-action-required';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending':  return 'PENDING';
      case 'active':   return 'IN PROGRESS';
      case 'resolved': return 'RESOLVED';
      default:         return (status || '').toUpperCase();
    }
  };

  const getSenderLabel = (sender) => {
    if (sender === 'superadmin')    return 'You (Superadmin)';
    if (sender === 'customer')      return 'Customer';
    if (sender === 'rentaladmin')   return 'Rental Admin';
    if (sender === 'mechanicadmin' || sender === 'mechanic') return 'Mechanic Admin';
    return sender;
  };

  const formatTime = (datetime) => {
    if (!datetime) return '';
    return new Date(datetime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const stats = {
    totalDisputes: disputes.length,
    actionRequired: disputes.filter(d => d.status === 'pending' || d.status === 'active').length,
    resolvedCount: disputes.filter(d => d.status === 'resolved').length,
  };

  return (
    <MasterLayout>
      <div className="dsp-disputes-page">
        <Container fluid>

          {isLoading ? (
            <>
              <SkeletonStatCards />
              <SkeletonTabs />
              <SkeletonTable />
            </>
          ) : (
            <>
              {/* Stats Cards */}
              <Row className="dsp-stats-row">
                <Col lg={4} md={6} className="mb-3">
                  <Card className="dsp-stat-card dsp-stat-warning">
                    <Card.Body>
                      <div className="dsp-stat-content">
                        <div className="dsp-stat-info">
                          <p className="dsp-stat-label">Total Forwarded Disputes</p>
                          <h2 className="dsp-stat-value">{stats.totalDisputes}</h2>
                        </div>
                        <div className="dsp-stat-icon dsp-icon-warning">
                          <FaExclamationTriangle />
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>

                <Col lg={4} md={6} className="mb-3">
                  <Card className="dsp-stat-card dsp-stat-danger">
                    <Card.Body>
                      <div className="dsp-stat-content">
                        <div className="dsp-stat-info">
                          <p className="dsp-stat-label">Action Required</p>
                          <h2 className="dsp-stat-value">{stats.actionRequired}</h2>
                        </div>
                        <div className="dsp-stat-icon dsp-icon-danger">
                          <MdWarning />
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>

                <Col lg={4} md={6} className="mb-3">
                  <Card className="dsp-stat-card dsp-stat-info">
                    <Card.Body>
                      <div className="dsp-stat-content">
                        <div className="dsp-stat-info">
                          <p className="dsp-stat-label">Resolved</p>
                          <h2 className="dsp-stat-value">{stats.resolvedCount}</h2>
                        </div>
                        <div className="dsp-stat-icon dsp-icon-info">
                          <FaClock />
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* ── Tabs ── */}
              <div className="dsp-outer-tabs-wrap">
                <div className="dsp-outer-tabs">
                  {TABS.map((tab) => {
                    const count = tab.status === 'all'
                      ? disputes.length
                      : disputes.filter(d => d.status === tab.status).length;
                    return (
                      <button
                        key={tab.status}
                        className={`dsp-outer-tab ${activeTab === tab.status ? 'dsp-outer-tab-active' : ''}`}
                        onClick={() => { setActiveTab(tab.status); setCurrentPage(1); }}
                      >
                        {tab.icon}
                        {tab.label}
                        <span className="dsp-outer-tab-count">({count})</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Disputes Table */}
              <Card className="dsp-table-card">
                <Card.Body>
                  <div className="dsp-table-header">
                    <h3 className="dsp-table-title">
                      {activeTab === 'all'      && 'All Forwarded Disputes'}
                      {activeTab === 'pending'  && 'Pending Disputes'}
                      {activeTab === 'active'   && 'In Progress Disputes'}
                      {activeTab === 'resolved' && 'Resolved Disputes'}
                    </h3>
                  </div>

                  <div className="dsp-table-wrapper">
                    <Table responsive className="dsp-disputes-table">
                      <thead>
                        <tr>
                          <th>DISPUTE ID</th>
                          <th>RAISED BY</th>
                          <th>AGAINST</th>
                          <th>CATEGORY</th>
                          <th>STATUS</th>
                          <th>DATE</th>
                          <th>ACTIONS</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedData.length > 0 ? (
                          paginatedData.map((dispute) => (
                            <tr key={dispute.id}>
                              <td>
                                <span className="dsp-dispute-id">#{dispute.id}</span>
                              </td>
                              <td>
                                <div className="dsp-person-info">
                                  <span className="dsp-person-name">{dispute.raisedby || '—'}</span>
                                </div>
                              </td>
                              <td>
                                <div className="dsp-person-info">
                                  <span className="dsp-person-name">{dispute.raisedto || '—'}</span>
                                </div>
                              </td>
                              <td>
                                <span className="dsp-category">{dispute.category}</span>
                              </td>
                              <td>
                                <span className={`dsp-status-badge ${getStatusBadgeClass(dispute.status)}`}>
                                  {getStatusText(dispute.status)}
                                </span>
                              </td>
                              <td>
                                <span className="dsp-date">{new Date(dispute.date).toLocaleDateString()}</span>
                              </td>
                              <td>
                                <Dropdown align="end">
                                  <Dropdown.Toggle variant="link" className="dsp-action-dropdown">
                                    <FaEllipsisV />
                                  </Dropdown.Toggle>
                                  <Dropdown.Menu className="dsp-dropdown-menu">
                                    <Dropdown.Item
                                      onClick={() => handleResolveClick(dispute)}
                                      className="dsp-dropdown-item dsp-resolve"
                                    >
                                      <FaCheckCircle className="me-2" />
                                      Resolve Dispute
                                    </Dropdown.Item>
                                    <Dropdown.Divider />
                                    <Dropdown.Item
                                      onClick={() => handleDeleteDispute(dispute.id)}
                                      className="dsp-dropdown-item dsp-delete"
                                    >
                                      <FaTrash className="me-2" />
                                      Delete
                                    </Dropdown.Item>
                                  </Dropdown.Menu>
                                </Dropdown>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="7" className="text-center py-5">
                              <FaExclamationTriangle size={48} color="#FDB714" />
                              <p className="mt-3 text-muted">No forwarded disputes found</p>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>

                  <AppPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={filteredDisputes.length}
                    itemsPerPage={itemsPerPage}
                    onPageChange={(page) => setCurrentPage(page)}
                  />
                </Card.Body>
              </Card>
            </>
          )}

          {/* Resolve Dispute Modal — 3-way group chat (dark header / card style) */}
          <Modal
            show={showResolveModal}
            onHide={handleCloseModal}
            size="lg"
            centered
            className="dsp-resolve-modal"
          >
            {selectedDispute && (
              <>
                {/* Dark Header */}
                <div className="dsp-rd-header">
                  <div className="dsp-rd-header-left">
                    <p className="dsp-rd-title">Resolve Dispute</p>
                    <p className="dsp-rd-sub">Full breakdown of dispute #{selectedDispute.id}</p>
                  </div>
                  <button className="dsp-rd-close" onClick={handleCloseModal}>
                    <FiX size={18} />
                  </button>
                </div>

                <div className="dsp-rd-body">
                  {/* Status + Date row */}
                  <div className="dsp-rd-status-row">
                    <span className={`dsp-status-badge ${getStatusBadgeClass(selectedDispute.status)}`}>
                      {getStatusText(selectedDispute.status)}
                    </span>
                    <span className="dsp-rd-date">
                      {new Date(selectedDispute.date).toLocaleDateString()}
                    </span>
                  </div>

                  {/* Parties */}
                  <p className="dsp-rd-section-title">Dispute Parties</p>
                  <div className="dsp-rd-parties-grid">
                    <div className="dsp-rd-party-card">
                      <span className="dsp-rd-party-label">Raised By</span>
                      <span className="dsp-rd-party-name">{selectedDispute.raisedby || '—'}</span>
                    </div>
                    <div className="dsp-rd-party-card">
                      <span className="dsp-rd-party-label">Against</span>
                      <span className="dsp-rd-party-name">{selectedDispute.raisedto || '—'}</span>
                    </div>
                  </div>

                  {/* Info Grid */}
                  <p className="dsp-rd-section-title">Dispute Information</p>
                  <div className="dsp-rd-info-grid">
                    <div className="dsp-rd-info-item">
                      <span className="dsp-rd-info-label">Category</span>
                      <span className="dsp-rd-info-value">{selectedDispute.category || '—'}</span>
                    </div>
                    <div className="dsp-rd-info-item">
                      <span className="dsp-rd-info-label">Date Filed</span>
                      <span className="dsp-rd-info-value">
                        {new Date(selectedDispute.date).toLocaleDateString()}
                      </span>
                    </div>
                    {selectedDispute.description && (
                      <div className="dsp-rd-info-item dsp-rd-info-full">
                        <span className="dsp-rd-info-label">Description</span>
                        <span className="dsp-rd-info-value">{selectedDispute.description}</span>
                      </div>
                    )}
                  </div>

                  {/* Chat */}
                  <div className="dsp-rd-chat-box">
                    <div className="dsp-rd-chat-header">
                      <BsRobot size={18} color="#000000" />
                      <p className="dsp-rd-chat-title">Dispute Resolution Chat</p>
                    </div>
                    <div className="dsp-chat-messages">
                      {chatMessages.length > 0 ? (
                        chatMessages.map((msg) => (
                          <div
                            key={msg.id}
                            className={`dsp-chat-message ${msg.sender === 'superadmin' ? 'dsp-admin-message' : 'dsp-user-message'}`}
                          >
                            <div className="dsp-message-content">
                              <div className="dsp-message-header">
                                <span className="dsp-sender-name">{getSenderLabel(msg.sender)}</span>
                                <span className="dsp-message-time">{formatTime(msg.created_at)}</span>
                              </div>
                              <p className="dsp-message-text">{msg.message}</p>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center text-muted py-3">No messages yet.</div>
                      )}
                      <div ref={messagesEndRef} />
                    </div>
                    <div className="dsp-chat-input-wrapper">
                      <Form.Control
                        type="text"
                        placeholder="Type your response to both parties..."
                        value={messageInput}
                        onChange={(e) => setMessageInput(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                        className="dsp-chat-input"
                      />
                      <Button onClick={handleSendMessage} className="dsp-send-button" disabled={!messageInput.trim() || !conversationId}>
                        <FaPaperPlane />
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="dsp-rd-footer">
                  <button className="dsp-rd-btn-close" onClick={handleCloseModal}>
                    Close
                  </button>
                  <button
                    className="dsp-rd-btn-resolve"
                    onClick={handleMarkAsResolved}
                    disabled={resolving || selectedDispute.status === 'resolved'}
                  >
                    <FaCheckCircle size={14} />
                    Mark as Resolved
                  </button>
                </div>
              </>
            )}
          </Modal>

          {/* Footer */}
          <div className="dsp-footer">
            <p className="dsp-footer-text">
              © 2023 Heavy Machinery Rental Portal - Disputes Administration
            </p>
          </div>

        </Container>
      </div>
    </MasterLayout>
  );
};

export default Disputes;